import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useState } from 'react';
import { MapPin, Droplets, Users, Calculator, PieChart, Star, Shield } from 'lucide-react';
import Header from './components/Header';
import ProgressBar from './components/ProgressBar';
import StateSelection from './components/StateSelection';
import HerbicideApplications from './components/HerbicideApplications';
import HerbicideDetails from './components/HerbicideDetails';
import HerbicideRating from './components/HerbicideRating';
import ManualWeeding from './components/ManualWeeding';
import Results from './components/Results';
import SubmissionsList from './components/SubmissionsList';

// The secret admin path - keep this private and share only with authorized users
const ADMIN_PATH = 'admin-dashboard-9x72m';

export interface Product {
  productName: string;
  cost: number;
}

export interface HerbicideApplication {
  type: 'solo' | 'tank-mix';
  products: Product[];
  sprayManCost: number;
  controlDuration: number;
}

export interface ManualWeedingDetail {
  laborers: number;
  costPerLaborer: number;
  controlDuration: number;
}

export interface FormData {
  state: string;
  district: string;
  croppingPattern: string;
  herbicideApplications: number;
  manualWeeding: number;
  herbicides: HerbicideApplication[];
  manualWeedingDetails: ManualWeedingDetail[];
  cropSafetyRating: number;
  herbicideEfficacyRating: number;
}

const initialFormData: FormData = {
  state: '',
  district: '',
  croppingPattern: '',
  herbicideApplications: 0,
  manualWeeding: 0,
  herbicides: [],
  manualWeedingDetails: [],
  cropSafetyRating: 5,
  herbicideEfficacyRating: 5,
};

const stepIcons = [
  <MapPin key="location" className="w-5 h-5" />,
  <Droplets key="applications" className="w-5 h-5" />,
  <Users key="herbicide" className="w-5 h-5" />,
  <Star key="rating" className="w-5 h-5" />,
  <Calculator key="manual" className="w-5 h-5" />,
  <PieChart key="results" className="w-5 h-5" />,
];

function CalculatorForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  const updateFormData = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setStep(1);
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.state !== '' && formData.district !== '' && formData.croppingPattern !== '';
      case 2:
        return formData.herbicideApplications >= 0 && formData.manualWeeding >= 0;
      case 3:
        return formData.herbicides.length === formData.herbicideApplications &&
          formData.herbicides.every(h => 
            h.products.every(p => p.productName && p.cost > 0) && 
            h.sprayManCost > 0 && 
            h.controlDuration > 0
          );
      case 4:
        return formData.cropSafetyRating > 0 && formData.herbicideEfficacyRating > 0;
      case 5:
        return formData.manualWeeding === 0 || (
          formData.manualWeedingDetails.length === formData.manualWeeding &&
          formData.manualWeedingDetails.every(w => 
            w.laborers > 0 && w.costPerLaborer > 0 && w.controlDuration > 0
          )
        );
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (isStepValid()) {
      if (step === 2) {
        if (formData.herbicideApplications > 0) {
          setStep(3);
        } else {
          setStep(5);
        }
      } else if (step === 3) {
        setStep(4);
      } else if (step === 4 && formData.manualWeeding === 0) {
        setStep(6);
      } else {
        setStep(step + 1);
      }
    }
  };

  const prevStep = () => {
    if (step === 6) {
      if (formData.manualWeeding === 0) {
        setStep(4);
      } else {
        setStep(5);
      }
    } else if (step === 5) {
      setStep(4);
    } else if (step === 4) {
      setStep(3);
    } else if (step === 3 && formData.herbicideApplications === 0) {
      setStep(2);
    } else {
      setStep(step - 1);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <StateSelection formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <HerbicideApplications formData={formData} updateFormData={updateFormData} />;
      case 3:
        return <HerbicideDetails formData={formData} updateFormData={updateFormData} />;
      case 4:
        return <HerbicideRating formData={formData} updateFormData={updateFormData} />;
      case 5:
        return <ManualWeeding formData={formData} updateFormData={updateFormData} />;
      case 6:
        return <Results formData={formData} resetForm={resetForm} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-brand-yellow/90">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <ProgressBar currentStep={step} totalSteps={6} icons={stepIcons} />

          <div className="backdrop-blur-md bg-white/30 rounded-2xl p-6 mt-8 border border-white/20 shadow-lg">
            {renderStep()}

            {step !== 6 && (
              <div className="flex justify-between mt-8">
                {step > 1 && (
                  <button
                    onClick={prevStep}
                    className="inline-flex items-center px-6 py-2.5 bg-white/30 backdrop-blur-sm border border-white/20 text-gray-700 rounded-full hover:bg-white/40 transition-all duration-200 font-medium"
                  >
                    Previous
                  </button>
                )}
                {step < 6 && (
                  <button
                    onClick={nextStep}
                    disabled={!isStepValid()}
                    className={`inline-flex items-center px-6 py-2.5 rounded-full ml-auto transition-all duration-200 font-medium ${
                      isStepValid()
                        ? 'bg-gradient-brand text-white shadow-lg shadow-brand-green/20 hover:shadow-brand-green/30 hover:scale-[1.02]'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/" element={<CalculatorForm />} />
          <Route path={`/${ADMIN_PATH}`} element={<SubmissionsList />} />
          {/* Redirect all other paths to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;